/**
 * Workspace AI Preferences Hooks
 *
 * Wraps Convex queries/mutations for managing workspace AI preferences.
 */

import { api } from "convex/_generated/api";
import { useApiMutation } from "@/packages/features-shared";
import type { Id } from "@/packages/types";
import { useWorkspaceQuery } from "@/hooks";
import { useTranslation } from "react-i18next";

/* ------------------------------------------------------------------ */
/* Types                                                                */
/* ------------------------------------------------------------------ */

export interface WorkspaceAiPreferencesData {
  _id: Id<"workspaceAiPreferences">;
  workspaceId: Id<"workspaces">;
  preferredLanguage?: string;
  tone?: "formal" | "conversational" | "technical";
  detailLevel?: "concise" | "standard" | "detailed";
  includeRiskRatings?: boolean;
  referenceClauseNumbers?: boolean;
  suggestRemediation?: boolean;
  includeEvidenceRequirements?: boolean;
  crossReferenceControls?: boolean;
  industryFrameworks: { name: string; customInstructions?: string }[];
  customInstructions?: string;
  createdAt: number;
  updatedAt: number;
}

/* ------------------------------------------------------------------ */
/* useWorkspaceAiPreferences                                            */
/* ------------------------------------------------------------------ */

interface UseWorkspaceAiPreferencesReturn {
  preferences: WorkspaceAiPreferencesData | null;
  isLoading: boolean;
}

export function useWorkspaceAiPreferences(): UseWorkspaceAiPreferencesReturn {
  const { data, isLoading } = useWorkspaceQuery(
    api.workspace_ai_preferences_queries.getForCurrentWorkspace,
    {},
  );

  return {
    preferences: data ?? null,
    isLoading,
  };
}

/* ------------------------------------------------------------------ */
/* useUpdateWorkspaceAiPreferences                                      */
/* ------------------------------------------------------------------ */

export function useUpdateWorkspaceAiPreferences() {
  const { t } = useTranslation("settings");
  const mutation = useApiMutation(api.workspace_ai_preferences_mutations.save, {
    name: "save-workspace-ai-preferences",
    successMessage: t("aiPreferences.saved"),
  });

  return {
    savePreferences: (args: {
      workspaceId: Id<"workspaces">;
      preferredLanguage?: string;
      tone?: "formal" | "conversational" | "technical";
      detailLevel?: "concise" | "standard" | "detailed";
      includeRiskRatings?: boolean;
      referenceClauseNumbers?: boolean;
      suggestRemediation?: boolean;
      includeEvidenceRequirements?: boolean;
      crossReferenceControls?: boolean;
      industryFrameworks: { name: string; customInstructions?: string }[];
      customInstructions?: string;
    }) => mutation.mutateAsync(args),
    isPending: mutation.isPending,
  };
}
